import { Locale as DateTimeLocale } from "date-fns/locale";
import { ComponentType, PropsWithChildren, useMemo } from "react";

import { LocalizerContext } from "@com.mgmtp.a12.utils/utils-localization-react/lib/main";
import {
	defaultDataFormats,
	defaultLocalizerFactory,
	defaultValueConversion,
	Locale,
	Localizer
} from "@com.mgmtp.a12.utils/utils-localization/lib/main";
import {
	A11YLanguageContext,
	getA11yResource
} from "@com.mgmtp.a12.widgets/widgets-core/lib/common/main/a11y-localization";
import { DateTimeContext } from "@com.mgmtp.a12.widgets/widgets-core/lib/common/main/date-time/date-time-context";

import { ModulFThemeProvider } from "@de.skhh.modulf/client-core";

import { DATE_TIME_LOCALES, DEFAULT_DATE_TIME_LOCALE } from "../../config/locale";
import { translationSource } from "../../localization";

export interface BaseWrapperProps {
	readonly locale: Locale;
}

export const BaseWrapper: ComponentType<PropsWithChildren<BaseWrapperProps>> = function BaseWrapper({
	locale,
	children
}) {
	const l10nContext = useMemo(() => {
		const dataFormats = defaultDataFormats(locale);
		const conversion = defaultValueConversion(dataFormats);
		const localizer = wrapCorrectionMapper(
			defaultLocalizerFactory({ locale, dataFormats, conversion, translationSource })
		);

		return { locale, dataFormats, conversion, localizer };
	}, [locale]);

	const a11yDefinition = useMemo(() => getA11yResource(locale.language), [locale.language]);

	const dateTimeContext = useMemo<{ locale: DateTimeLocale }>(() => {
		const map: Record<string, DateTimeLocale> = DATE_TIME_LOCALES;
		const l = map[Locale.toString(locale)] ?? DEFAULT_DATE_TIME_LOCALE;
		return { locale: l };
	}, [locale]);

	return (
		<LocalizerContext.Provider value={l10nContext}>
			<DateTimeContext.Provider value={dateTimeContext}>
				<ModulFThemeProvider compact>
					<A11YLanguageContext.Provider value={a11yDefinition}>{children}</A11YLanguageContext.Provider>
				</ModulFThemeProvider>
			</DateTimeContext.Provider>
		</LocalizerContext.Provider>
	);
};

function wrapCorrectionMapper(localizer: Localizer): Localizer {
	const correctionMap: Record<string, string> = {
		// Error text in the print editor component
		"Bitte wählen sie eine eingangsquelle": "Bitte wählen Sie eine Eingangsquelle",
		// Hint text in the print editor component
		"Benutzereingabe eingebent": "Benutzereingabe eingeben",
		// Label in the print editor component
		Formattierungstyp: "Formatierungstyp"
	};

	return function customLocalizer(...localizable) {
		const value = localizer(...localizable);
		return value ? (correctionMap[value] ?? value) : value;
	};
}
